import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import {
	getLocalisationBundle,
	getLocalisedCaptions,
	getUserLocalePreference,
	getLocaleSettings,
} from "services/localisation";
import enResources from "./resources.en.json";
import {
	APP_LOCALE_KEY,
	DEFAULT_LOCALE,
	I18N_NAMESPACE,
	LOCALE_STORAGE_KEY,
	PSEUDO_LOCALE,
	PSEUDO_TERMS_LOCALE,
	bundleMessagesToResources,
	getActivePseudoTag,
	isPseudoLocaleActive,
	isPseudoTag,
	isPseudoLocaleEnvironment,
	mapCaptionCCFields,
	pseudoizeCaptionCCFields,
	pseudoizeCustomCaptions,
	resolveInitialLocale,
} from "./localisationResources";
import {
	installPseudoStorageShim,
	scrubPseudoCaptionsFromStorage,
	setActiveLocaleCaptions,
} from "./pseudoStorageShim";

// Must run before anything reads the me blob so caption reads are already
// pseudo-aware on the first render. No-op outside local/dev.
installPseudoStorageShim();

export {
	DEFAULT_LOCALE,
	PSEUDO_LOCALE,
	PSEUDO_TERMS_LOCALE,
	I18N_NAMESPACE,
	getActivePseudoTag,
	isPseudoLocaleActive,
	isPseudoLocaleEnvironment,
} from "./localisationResources";

// i18next backend plugin backed by GET /api/localisation/bundle. The server
// merges site overrides + <locale>.po + parent locale + en.po + common.po
// into one flat payload, so a single read per language is enough. Requesting
// locale=x-pseudo returns server-generated pseudo-translated English.
const bundleBackend = {
	type: "backend",
	init() {},
	read(language, _namespace, callback) {
		getLocalisationBundle(language)
			.then((response) => {
				if (!response?.status || !response?.data?.messages) {
					callback(
						new Error(`Failed to load localisation bundle for ${language}`),
						false
					);
					return;
				}

				callback(null, bundleMessagesToResources(response.data.messages));
			})
			.catch((err) => callback(err, false));
	},
};

// Applies a bundle returned inline by the override save/reset endpoints so
// changed terminology shows immediately without a reload. deep=false replaces
// the whole language resource set (a bundle is always complete), and the
// store "added" event re-renders subscribed useTranslation components.
export const applyLocalisationBundle = (bundle) => {
	if (!bundle?.locale || !bundle?.messages) {
		return;
	}

	i18n.addResourceBundle(
		bundle.locale,
		I18N_NAMESPACE,
		bundleMessagesToResources(bundle.messages),
		false,
		true
	);
};

// Cycles OFF -> pseudo (all keys) -> pseudo terms-only -> OFF, persisting the
// choice for the session. Reloads the page rather than calling changeLanguage
// in place: components snapshot me.customCaptions at mount, so an in-place
// switch would leave every already-rendered caption unchanged and only affect
// live t() strings — a reload guarantees the whole app re-reads through the
// pseudo transform. QA tooling; only reachable in pseudo-capable environments
// (see isPseudoLocaleEnvironment).
export const togglePseudoLocale = () => {
	// The persisted flag is the source of truth, NOT i18n.language - anything
	// that changes the language in place (locale resolution, failures) would
	// otherwise flip the toggle's direction and strand pseudo on.
	const current = getActivePseudoTag();
	const next =
		current === null
			? PSEUDO_LOCALE
			: current === PSEUDO_LOCALE
				? PSEUDO_TERMS_LOCALE
				: DEFAULT_LOCALE;

	try {
		window.sessionStorage.setItem(LOCALE_STORAGE_KEY, next);
	} catch {
		// Storage unavailable (private mode/quota) — fall back to an in-place
		// switch for the current page lifetime.
		return i18n.changeLanguage(next);
	}

	// Turning OFF: heal any pseudo-wrapped captions that got baked into the
	// stored me blob while pseudo was active (must run after the flag flip so
	// the storage shim reads/writes raw values, and before the reload below).
	if (next === DEFAULT_LOCALE) {
		scrubPseudoCaptionsFromStorage();
	}

	// Hand control to the persisted choice by dropping any ?locale= from the URL.
	// A deep-linked ?locale=x-pseudo takes precedence over storage (resolveInitialLocale
	// / isPseudoLocaleActive), so without this the toggle could never turn pseudo off
	// while that param stayed in the address bar.
	const url = new URL(window.location.href);
	url.searchParams.delete("locale");
	window.location.assign(url.toString());
	return Promise.resolve();
};

// The chosen UI locale persisted in localStorage (all environments); survives
// reloads so the site's language sticks.
export const getPersistedAppLocale = () => {
	try {
		return window.localStorage.getItem(APP_LOCALE_KEY);
	} catch {
		return null;
	}
};

// A user session exists once the auth "me" blob is stored. Pre-auth screens
// (login, forgot/reset password, register) are ALWAYS English regardless of any
// locale persisted by a previous session — language is a per-user, post-login
// concept.
export const isAuthenticated = () => {
	try {
		return !!(
			window.sessionStorage.getItem("me") || window.localStorage.getItem("me")
		);
	} catch {
		return false;
	}
};

// The stored auth blob (session first, then local). Language context reads
// `siteAppID` from it: set only once a site app is the active working context.
const getStoredMe = () => {
	try {
		return JSON.parse(
			window.sessionStorage.getItem("me") ||
				window.localStorage.getItem("me") ||
				"null"
		);
	} catch {
		return null;
	}
};

// Management routes (portal, client/application admin, own profile, AI config)
// are never a site-app operational context — even though a prior site-app
// session leaves `siteAppID` in storage, language on these screens is the
// user's own preference. Every other authenticated route (services, models,
// defects, feedback, analytics, noticeboards, in-app user management, …) is a
// site-app operational screen.
const MANAGEMENT_ROUTE_PREFIXES = [
	"/app/portal",
	"/app/clients",
	"/app/client",
	"/app/applications",
	"/app/me",
	"/app/aiconfiguration",
];

const isManagementRoute = () => {
	try {
		const path = window.location.pathname;
		return MANAGEMENT_ROUTE_PREFIXES.some(
			(p) => path === p || path.startsWith(`${p}/`)
		);
	} catch {
		return false;
	}
};

// True while a site app is the active *operational* context: a site app has
// been entered (siteAppID in storage) AND the current screen is one of its
// operational screens. On management routes it is false regardless of the
// stored siteAppID, so language there is the user's preference verbatim.
export const isSiteAppContext = () =>
	!!getStoredMe()?.siteAppID && !isManagementRoute();

// The effective UI locale for the signed-in user. Language is a USER-level
// setting (their own preferred language, English by default):
//   - Outside a site app (portal, client & admin/settings screens) it applies
//     verbatim — every language the build ships is selectable.
//   - Inside a site app it applies only if that site app offers it; otherwise
//     the site app's own default language wins.
export const resolveEffectiveUserLocale = async () => {
	const prefRes = await getUserLocalePreference().catch(() => null);
	const preferred =
		(prefRes?.status && prefRes.data?.locale) || DEFAULT_LOCALE;

	// Portal / client / admin: the user's preference is authoritative.
	if (!isSiteAppContext()) return preferred;

	// Site app: gate the preference by the site app's enabled locales.
	const settingsRes = await getLocaleSettings().catch(() => null);
	const enabled = settingsRes?.status
		? settingsRes.data?.enabledLocales ?? [DEFAULT_LOCALE]
		: [DEFAULT_LOCALE];
	const siteDefault = settingsRes?.status
		? settingsRes.data?.defaultLocale ?? DEFAULT_LOCALE
		: DEFAULT_LOCALE;

	return enabled.includes(preferred) ? preferred : siteDefault;
};

// Resolve + apply the effective locale for the signed-in user in the current
// site app, and persist it as the active app locale. Pre-auth is a no-op (stays
// English). At startup (reload:false) it applies in place before the first paint;
// at runtime (reload:true — after login, a site-app switch, or a profile change)
// it reloads only when the language actually changes, because mounted screens
// snapshot me.customCaptions and must re-read through the new locale.
export const resolveAndApplyUserLocale = async ({ reload = true } = {}) => {
	if (!isAuthenticated()) return DEFAULT_LOCALE;

	// An active pseudo-locale choice (dev QA overlay) wins over the user/site
	// locale. Without this guard the signed-in boot resets i18n to the real
	// locale while the storage shim keeps pseudo-izing captions off the session
	// flag - the app splits into two languages and the toggle (which reads the
	// flag) desyncs from t() strings. The user's real preference applies again
	// the moment pseudo is switched off (which reloads).
	const activePseudoTag = getActivePseudoTag();
	if (activePseudoTag) return activePseudoTag;

	let effective;
	try {
		effective = await resolveEffectiveUserLocale();
	} catch {
		return getPersistedAppLocale() || DEFAULT_LOCALE;
	}

	try {
		window.localStorage.setItem(APP_LOCALE_KEY, effective);
	} catch {
		// storage unavailable — apply for this page lifetime only
	}

	const changed = effective !== i18n.language;

	if (reload) {
		if (changed) {
			window.location.reload();
		} else if (effective === DEFAULT_LOCALE) {
			setActiveLocaleCaptions(null);
		}
		return effective;
	}

	// Startup path (pre-first-paint): apply in place, no reload.
	if (effective !== DEFAULT_LOCALE) {
		await applyLocaleCaptions(effective);
	} else {
		setActiveLocaleCaptions(null);
	}
	if (changed) {
		try {
			await i18n.changeLanguage(effective);
		} catch {
			// keep current language on failure
		}
	}
	return effective;
};

// Force the UI back to English and drop any locale caption overlay. Called on
// logout so the login page — and the next sign-in's pre-auth screens — always
// start in English, whatever language the previous session was using. Logout
// is an SPA navigation (no reload), so without this the already-loaded i18n
// would keep rendering the old language on the login page.
export const resetLocaleToDefault = async () => {
	try {
		window.localStorage.removeItem(APP_LOCALE_KEY);
		window.sessionStorage.removeItem(APP_LOCALE_KEY);
	} catch {
		// storage unavailable — still reset the in-memory language below
	}
	setActiveLocaleCaptions(null);
	if (i18n.language !== DEFAULT_LOCALE) {
		try {
			await i18n.changeLanguage(DEFAULT_LOCALE);
		} catch {
			// keep current language on failure
		}
	}
};

// Startup locale: an ACTIVE dev pseudo choice wins, then the persisted app
// locale, then English. The pseudo toggle writes "en" into its session key when
// switched off, so only honour that key when it's actually the pseudo locale —
// otherwise a stale "en" there would shadow the user's real language choice.
const resolveStoredLocale = () => {
	if (isPseudoLocaleEnvironment()) {
		try {
			const pseudo = window.sessionStorage.getItem(LOCALE_STORAGE_KEY);
			if (isPseudoTag(pseudo)) return pseudo;
		} catch {
			// ignore
		}
	}

	// Not signed in → English. The login and other pre-auth pages never show a
	// previously-persisted language.
	if (!isAuthenticated()) return DEFAULT_LOCALE;

	return getPersistedAppLocale();
};

const storedLocale = resolveStoredLocale();

// Caption-driven UI (the ~1,476 me.customCaptions consumers) doesn't go through
// t(), so switching i18next alone wouldn't change it. Fetch the authoritative
// localised captions for the active locale from the backend, register them as
// the storage overlay (so every future write to me — login, token refresh,
// portal switch, … — stays localised), and patch the current stored me blob.
// English clears the overlay.
export const applyLocaleCaptions = async (locale) => {
	if (locale === DEFAULT_LOCALE) {
		setActiveLocaleCaptions(null);
	}

	const response = await getLocalisedCaptions(locale);
	if (!response?.status || !response?.data) return;

	const localeCaptions = response.data;
	if (Object.keys(localeCaptions).length === 0) return;

	if (locale !== DEFAULT_LOCALE) {
		setActiveLocaleCaptions(localeCaptions);
	}

	[window.sessionStorage, window.localStorage].forEach((store) => {
		try {
			const raw = store.getItem("me");
			if (!raw) return;
			const me = JSON.parse(raw);
			if (!me) return;
			me.customCaptions = { ...(me.customCaptions ?? {}), ...localeCaptions };
			store.setItem("me", JSON.stringify(me));
		} catch {
			// ignore malformed/absent me
		}
	});
};

// Translates a single entity term to the active locale via the loaded bundle,
// returning the fallback when the key isn't present.
const translateEntityCaption = (base, isPlural, fallback) => {
	const key = `entity.${base}`;
	if (!i18n.exists(key)) return fallback;
	return i18n.t(key, { count: isPlural ? 2 : 1 });
};

// Localises a site-app detail payload's *CC caption fields (the admin screens
// read these from SiteApplicationContext, not from me.customCaptions). Pseudo
// wins when active; English passes through unchanged.
export const localiseCaptionPayload = (payload) => {
	if (isPseudoLocaleActive()) return pseudoizeCaptionCCFields(payload);
	if (i18n.language === DEFAULT_LOCALE) return payload;

	return mapCaptionCCFields(payload, (field, str) => {
		const isPlural = field.endsWith("PluralCC");
		const base = isPlural
			? field.slice(0, -"PluralCC".length)
			: field.slice(0, -"CC".length);
		return translateEntityCaption(base, isPlural, str);
	});
};

// Localises the defaultCustomCaptionsData map (plain prop names: asset,
// assetPlural, …) the same way.
export const localiseDefaultCaptions = (captions) => {
	if (isPseudoLocaleActive()) return pseudoizeCustomCaptions(captions);
	if (i18n.language === DEFAULT_LOCALE || !captions) return captions;

	return Object.fromEntries(
		Object.entries(captions).map(([prop, val]) => {
			if (typeof val !== "string" || val === "") return [prop, val];
			const isPlural = prop.endsWith("Plural");
			const base = isPlural ? prop.slice(0, -"Plural".length) : prop;
			return [prop, translateEntityCaption(base, isPlural, val)];
		})
	);
};

// Switches the whole app (this site) to a locale: persist the choice, localise
// the stored captions, then reload so every screen re-reads. QA/admin action.
export const setAppLocale = async (locale) => {
	try {
		window.localStorage.setItem(APP_LOCALE_KEY, locale);
	} catch {
		// storage unavailable — still switch for this page lifetime
	}

	await applyLocaleCaptions(locale);
	window.location.reload();
};

if (!i18n.isInitialized) {
	i18n.use(bundleBackend)
		.use(initReactI18next)
		.init({
			lng: resolveInitialLocale(window.location.search, storedLocale),
			fallbackLng: DEFAULT_LOCALE,
			load: "currentOnly",
			ns: [I18N_NAMESPACE],
			defaultNS: I18N_NAMESPACE,
			// English shipped in the build (generated from en.po by
			// scripts/generate-en-resources.js): gives t() real values in unit
			// tests and on first paint before the API bundle loads. The bundle
			// backend still fetches the active locale and overrides at runtime.
			resources: { [DEFAULT_LOCALE]: { [I18N_NAMESPACE]: enResources } },
			partialBundledLanguages: true,
			// Resource keys are full dotted keys ("entity.asset"), not trees.
			keySeparator: false,
			nsSeparator: false,
			interpolation: { escapeValue: false },
			react: {
				useSuspense: false,
				bindI18nStore: "added removed",
			},
		});
}

// Console access for manual QA (window.i18n.t(...)); dev builds only.
if (process.env.NODE_ENV === "development") {
	window.i18n = i18n;
}

export default i18n;
